import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { Grid, Typography } from "@mui/material";
import { useState } from "react";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTextField from "../../../components/CustomTextField";
import Select from "../../../components/Select/Select";
import "../BranchManagement/BranchManagement.css";
import "./GlobalSettings.css";

const roleOptions = [
  "Front Desk",
  "Billing",
  "Practice Manager",
  "Clinic Owner",
  "Super Admin",
];
const userOptions = ["Supervet", "Ranjan", "Sanjai", "Sashvanth"];
const delegateOptions = [
  { name: "Practice Manager", value: "Practice Manager" },
  { name: "Clinic Owner", value: "Clinic Owner" },
];

const LeaveAttendanceSettings = () => {
  const [leaveTypes, setLeaveTypes] = useState(["Personal", "Sick", "Annual"]);
  const [newLeaveType, setNewLeaveType] = useState("");

  const [durationTiers, setDurationTiers] = useState([
    {
      id: 1,
      min: 0,
      max: 2,
      autoApprove: true,
      approverRoles: [],
      approverUsers: [],
    },
    {
      id: 2,
      min: 2,
      max: 5,
      autoApprove: false,
      approverRoles: ["Practice Manager"],
      approverUsers: [],
    },
    {
      id: 3,
      min: 5,
      max: 30,
      autoApprove: false,
      approverRoles: ["Practice Manager"],
      approverUsers: ["Supervet"],
    },
  ]);

  const [delegationEnabled, setDelegationEnabled] = useState(true);
  const [delegateTo, setDelegateTo] = useState("Practice Manager");
  const [delegateFromDate, setDelegateFromDate] = useState("");
  const [delegateToDate, setDelegateToDate] = useState("");
  const [notifyDelegate, setNotifyDelegate] = useState(true);
  const [notifyRequester, setNotifyRequester] = useState(false);

  const handleAddLeaveType = () => {
    if (!newLeaveType.trim() || leaveTypes.includes(newLeaveType.trim()))
      return;
    setLeaveTypes((prev) => [...prev, newLeaveType.trim()]);
    setNewLeaveType("");
  };

  const handleRemoveLeaveType = (type) => {
    setLeaveTypes((prev) => prev.filter((t) => t !== type));
  };

  const handleAddTier = () => {
    const lastTier = durationTiers[durationTiers.length - 1];
    const newMin = lastTier ? lastTier.max : 0;
    const newMax = newMin + 5;
    setDurationTiers((prev) => [
      ...prev,
      {
        id: Date.now(),
        min: newMin,
        max: newMax,
        autoApprove: false,
        approverRoles: [],
        approverUsers: [],
      },
    ]);
  };

  const handleRemoveTier = (tierId) => {
    setDurationTiers((prev) => prev.filter((t) => t.id !== tierId));
  };

  const updateTierField = (tierId, key, value) => {
    setDurationTiers((prev) =>
      prev.map((t) => (t.id === tierId ? { ...t, [key]: value } : t)),
    );
  };

  const toggleTierRole = (tierId, role) => {
    setDurationTiers((prev) =>
      prev.map((t) => {
        if (t.id !== tierId) return t;
        const exists = t.approverRoles.includes(role);
        return {
          ...t,
          approverRoles: exists
            ? t.approverRoles.filter((r) => r !== role)
            : [...t.approverRoles, role],
        };
      }),
    );
  };

  const toggleTierUser = (tierId, user) => {
    setDurationTiers((prev) =>
      prev.map((t) => {
        if (t.id !== tierId) return t;
        const exists = t.approverUsers.includes(user);
        return {
          ...t,
          approverUsers: exists
            ? t.approverUsers.filter((u) => u !== user)
            : [...t.approverUsers, user],
        };
      }),
    );
  };

  const renderDaysField = (tier, field) => (
    <div className="bf-amount-input la-days-input">
      <CustomTextField
        type="number"
        value={tier[field]}
        handleChange={(e) =>
          updateTierField(tier.id, field, Number(e?.target?.value))
        }
        fullWidth
      />
      <span className="bf-amount-suffix">days</span>
    </div>
  );

  return (
    <div className="consultation-white-con scroll-80vh p20">
      <div className="flex-row-between-align-center mb20 pb10 border-bottom">
        <div>
          <Typography className="text-bold fs20 black">
            Leave & Attendance
          </Typography>
          <Typography className="txt-regular fs13 card-gray-color mt4">
            Leave types, approval-by-duration rules, and delegation coverage —
            configured per branch.
          </Typography>
        </div>
        <CustomButton text="Save Changes" smallBtn />
      </div>

      {/* 1. Leave Types */}
      <div className="bf-approval-card mb20">
        <Typography className="text-bold fs15 black mb3">
          Leave types
        </Typography>
        <Typography className="txt-regular fs12 card-gray-color mb15">
          Shown as options when staff submit a leave request.
        </Typography>

        <div className="flex-row flex-wrap gap10 mb15">
          {leaveTypes.map((type) => (
            <span key={type} className="la-type-chip">
              {type}
              <button
                type="button"
                className="la-type-remove"
                onClick={() => handleRemoveLeaveType(type)}
                aria-label={`Remove ${type}`}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </span>
          ))}
        </div>

        <div className="la-add-row">
          <CustomTextField
            placeholder="e.g. Bereavement"
            value={newLeaveType}
            handleChange={(e) => setNewLeaveType(e.target.value)}
            fullWidth
          />
          <button
            type="button"
            className="la-add-btn"
            onClick={handleAddLeaveType}
            aria-label="Add leave type"
          >
            <AddIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
      </div>

      {/* 2. Approval by Duration */}
      <div className="bf-approval-card mb20">
        <Typography className="text-bold fs15 black mb3">
          Approval by duration
        </Typography>
        <Typography className="txt-regular fs12 card-gray-color mb15">
          Shorter leave can auto-approve; longer leave routes to an approver —
          role, specific person, or both.
        </Typography>

        {durationTiers.map((tier, idx) => (
          <div className="bf-tier-card" key={tier.id}>
            <div className="bf-tier-top">
              <span className="bf-tier-badge">Tier {idx + 1}</span>

              <div className="bf-amount-range">
                {renderDaysField(tier, "min")}
                <ArrowForwardIcon
                  sx={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }}
                />
                {renderDaysField(tier, "max")}
              </div>

              <button
                type="button"
                className={`la-auto-approve${
                  tier.autoApprove ? " la-auto-approve-active" : ""
                }`}
                onClick={() =>
                  updateTierField(tier.id, "autoApprove", !tier.autoApprove)
                }
              >
                <span className="la-auto-check">
                  {tier.autoApprove ? "✓" : ""}
                </span>
                Auto-approve
              </button>

              <button
                type="button"
                className="ct-task-remove-btn"
                onClick={() => handleRemoveTier(tier.id)}
                aria-label={`Remove tier ${idx + 1}`}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </button>
            </div>

            {!tier.autoApprove && (
              <>
                <div className="mt15">
                  <Typography className="txt-regular fs12 card-gray-color mb8">
                    By role
                  </Typography>
                  <div className="bf-chip-row">
                    {roleOptions.map((role) => {
                      const selected = tier.approverRoles.includes(role);
                      return (
                        <button
                          key={role}
                          type="button"
                          className={`bf-role-chip${
                            selected ? " bf-role-chip-active" : ""
                          }`}
                          onClick={() => toggleTierRole(tier.id, role)}
                        >
                          <span className="bf-chip-check">
                            {selected ? "✓" : ""}
                          </span>
                          {role}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt15">
                  <Typography className="txt-regular fs12 card-gray-color mb8">
                    By specific person (optional, adds to whichever role
                    approvers already cover)
                  </Typography>
                  <div className="bf-chip-row">
                    {userOptions.map((user) => {
                      const selected = tier.approverUsers.includes(user);
                      return (
                        <button
                          key={user}
                          type="button"
                          className={`bf-person-chip${
                            selected ? " bf-person-chip-active" : ""
                          }`}
                          onClick={() => toggleTierUser(tier.id, user)}
                        >
                          <span className="bf-chip-check">
                            {selected ? "✓" : ""}
                          </span>
                          {user}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        ))}

        <button
          type="button"
          className="bf-add-tier-btn"
          onClick={handleAddTier}
        >
          <AddIcon sx={{ fontSize: 18 }} />
          Add threshold tier
        </button>
      </div>

      {/* 3. Delegation */}
      <div className="bf-approval-card">
        <div className="flex-row-between-align-center">
          <div>
            <Typography className="text-bold fs15 black">
              Delegate approvals when the approver is away
            </Typography>
            <Typography className="txt-regular fs12 card-gray-color mt2">
              Keeps leave requests moving when the usual approver is out of
              office.
            </Typography>
          </div>
          <CustomSwitch
            grayToBlue
            value={delegationEnabled}
            onChange={(e) => setDelegationEnabled(e?.target?.value)}
          />
        </div>

        {delegationEnabled && (
          <div className="mt20">
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography className="txt-semi-bold fs12 card-gray-color mb5">
                  Delegate to
                </Typography>
                <Select
                  list={delegateOptions}
                  value={delegateTo}
                  handleChange={(e) => setDelegateTo(e?.target?.value)}
                  select
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="From"
                  type="date"
                  value={delegateFromDate}
                  handleChange={(e) => setDelegateFromDate(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="To"
                  type="date"
                  value={delegateToDate}
                  handleChange={(e) => setDelegateToDate(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <div className="flex-row flex-wrap gap20 mt5">
                  <div className="flex-row-ali-cen">
                    <CustomCheckbox
                      checked={notifyDelegate}
                      onChange={() => setNotifyDelegate((v) => !v)}
                    />
                    <Typography className="txt-regular fs13 black ml8">
                      Notify delegate when active
                    </Typography>
                  </div>
                  <div className="flex-row-ali-cen">
                    <CustomCheckbox
                      checked={notifyRequester}
                      onChange={() => setNotifyRequester((v) => !v)}
                    />
                    <Typography className="txt-regular fs13 black ml8">
                      Notify requester of delegation
                    </Typography>
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveAttendanceSettings;
